import { fetchPreviewBlob, getDocumentUrl, ApiCallError, type PreviewTarget } from '@/api/client';

/**
 * ============================================================================
 * ⭐⭐ PRINTING A SCANNED DOCUMENT — THE OS DIALOG, NOT A DOWNLOAD
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"The print icon automatically downloads the doc to
 * > downloads folder … I think in the meantime, we have the Print icon open all
 * > Adobe's in Reader or Word/Excel on the desktop if possible."*
 *
 * ---- ⛔⛔ IT IS NOT POSSIBLE, AND THE REASON MATTERS ----------------------
 *
 * **A WEB PAGE CANNOT LAUNCH A DESKTOP APPLICATION.** No browser will start
 * Acrobat, Word or Excel — that is the same sandbox that makes a Zebra need a
 * local agent at all (§1AK). ⚠️ There is no flag, no permission and no
 * enterprise policy that changes it; a page that could start `.exe`s would be
 * the whole security model gone.
 *
 * ⭐ **SO THE HONEST BEST IS THE OS PRINT DIALOG**, which lists every printer
 * the workstation has, laser included. One click from paper, no download, no
 * Acrobat. That is §1AK's `DIALOG` path, and this is it.
 *
 * ---- ⛔ WHY THE CURRENT BUTTON DOWNLOADS ----------------------------------
 *
 * `getDocumentUrl` mints a storage SAS, and **`blobClient` stamps every SAS
 * `Content-Disposition: attachment`** on purpose — a file rendered INLINE runs
 * in the origin that served it, and paperwork is uploaded by whoever has a
 * scanner. ⚠️ `window.open` on an `attachment` URL is a download by definition.
 * The old behaviour was not a bug in the button; it was the button asking the
 * wrong endpoint.
 *
 * ⭐ **THE PREVIEW ROUTE ALREADY SERVES BYTES INLINE AND SAFELY** — through
 * `previewTypes.ts`, which is an allow-list of things that cannot carry script
 * (pdf + raster, `.svg` excluded on purpose). `fetchPreviewBlob` turns those
 * bytes into a **same-origin `blob:` URL**, and a same-origin frame is the one
 * thing `print()` can actually be called on.
 *
 * ---- ⚠️ THREE OUTCOMES, AND TWO OF THEM ARE NOT FAILURES -----------------
 *
 *   `dialog`   — the print dialog opened. What we want.
 *   `tab`      — the browser would not print the frame, so the document opened
 *                in its own tab; print from the viewer's own button.
 *   `download` — ⛔ the type cannot be rendered by a browser at all (`.docx`,
 *                `.xlsx`). Nothing can print it without the desktop app, so it
 *                is downloaded and the message says why.
 *
 * ⛔ EVERY PATH RETURNS A SENTENCE AND NONE OF THEM THROWS. Same rule as
 * `useZebraPrint`: a print that quietly does nothing is the worst outcome.
 * ============================================================================
 */

export type PrintVia = 'dialog' | 'tab' | 'download';

export interface DocumentPrintOutcome {
    ok: boolean;
    via: PrintVia;
    message: string;
}

/**
 * ⚠️ ONLY THESE CAN BE PRINTED FROM A FRAME. It deliberately mirrors
 * `previewTypes.ts` rather than importing it — that file is server-side, and
 * the browser must not assume what the server allows. ⛔ If they drift, the
 * server wins: a type absent there never reaches us as a blob at all.
 */
function framePrintable(contentType: string): boolean {
    return contentType === 'application/pdf' || contentType.startsWith('image/');
}

/**
 * ---- ⛔⛔ THE HIDDEN FRAME, AND WHY IT IS REMOVED ON A TIMER --------------
 *
 * `print()` is **synchronous-ish and unobservable**: there is no event for "the
 * user dismissed the dialog", and `onafterprint` does not fire reliably for a
 * frame in every browser. ⚠️ Revoking the object URL or removing the frame too
 * early leaves the dialog printing a blank page — which looks exactly like a
 * broken printer.
 *
 * ⭐ SO IT IS LEFT IN PLACE FOR A GENEROUS WINDOW and then cleaned up. The cost
 * of being late is a few hundred KB held briefly; the cost of being early is a
 * blank sheet and an hour of confusion.
 */
function printInFrame(url: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.right = '0';
        frame.style.bottom = '0';
        frame.style.width = '1px';
        frame.style.height = '1px';
        frame.style.border = '0';
        frame.style.opacity = '0';
        /* ⚠️ NOT `display:none`. A display:none frame is not laid out, and some
           browsers refuse to print a frame that was never rendered. */
        frame.setAttribute('aria-hidden', 'true');

        let settled = false;
        const cleanup = () => {
            window.setTimeout(() => {
                try { frame.remove(); } catch { /* already gone */ }
                try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
            }, 60_000);
        };

        /* ⛔ A LOAD THAT NEVER FIRES MUST NOT HANG THE BUTTON. Same lesson as the
           Zebra agent probe: the failure mode of an un-fired callback is a
           control that spins for ever, which is worse than one that reports a
           fallback. */
        const timer = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(false);
        }, 8000);

        frame.onload = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            try {
                const w = frame.contentWindow;
                if (!w) { cleanup(); resolve(false); return; }
                w.focus();
                w.print();
                cleanup();
                resolve(true);
            } catch {
                /* ⚠️ Chrome and Safari have both, at times, refused print() on a
                   blob-backed PDF frame. That is a fallback, not an error. */
                cleanup();
                resolve(false);
            }
        };

        frame.src = url;
        document.body.appendChild(frame);
    });
}

/**
 * Print one stored document, or say exactly why it could not be printed.
 *
 * ⛔ NEVER THROWS. Every caller is an icon button and needs a sentence.
 */
export async function printDocumentToDialog(
    getToken: () => Promise<string>,
    target: PreviewTarget,
    fileName: string,
): Promise<DocumentPrintOutcome> {
    let url: string | null = null;
    let contentType = '';

    try {
        const got = await fetchPreviewBlob(await getToken(), target);
        url = got.url;
        contentType = got.contentType;
    } catch (err) {
        /**
         * ⚠️ THE PREVIEW ROUTE REFUSING IS THE EXPECTED PATH FOR OFFICE FILES,
         * not an error. It means "a browser cannot render this", and a browser
         * that cannot render something certainly cannot print it. ⭐ Falling back
         * to the download is the only thing left that helps — and the message has
         * to say that the desktop app is now required, or somebody will click
         * print four more times.
         */
        try {
            const { url: sas } = await getDocumentUrl(
                await getToken(),
                target.kind === 'document' ? target.documentId : '');
            window.open(sas, '_blank', 'noopener,noreferrer');
            return {
                ok: false,
                via: 'download',
                message: `${fileName} is not a type a browser can print (only PDFs and images are). `
                       + 'It has been downloaded — open it in Acrobat, Word or Excel and print from there.',
            };
        } catch {
            const why = err instanceof ApiCallError ? err.details.error : 'it could not be read';
            return { ok: false, via: 'download', message: `${fileName} could not be printed: ${why}.` };
        }
    }

    return printUrlToDialog(url, contentType, fileName);
}

/**
 * ---- ⭐⭐ PRINT BYTES THE BROWSER IS ALREADY HOLDING (2026-09-10) ----------
 *
 * > Pete: *"Where are my other icons next to the eye? Like Delete?"* — the local
 * > tray had a preview and nothing else, while the SharePoint tray beside it had
 * > print and bin.
 *
 * ⛔ `printDocumentToDialog` COULD NOT SERVE THAT ROW. It starts by fetching
 * `documentId` through the preview route, and a scan sitting in a folder on the
 * scheduler's PC HAS no documentId — it is not in Dock. ⚠️ Calling it with a
 * made-up target fails at the server with a sentence about a document nobody
 * asked about.
 *
 * ⭐ SO THE HALF THAT ACTUALLY PRINTS IS SPLIT OUT AND SHARED. Everything from
 * here down never cared where the bytes came from — an object URL from
 * `createObjectURL` prints exactly like one from the preview route, with no
 * network at all.
 *
 * ⛔ ONE IMPLEMENTATION, TWO CALLERS. A second copy of the frame/tab fallback
 * ladder is how the two trays would come to disagree about what happens to a
 * TIFF — which is §4.14.1 in the file that exists to stop it.
 *
 * ⚠️ THE CALLER OWNS THE URL'S LIFETIME for the tab paths; `printInFrame`
 * schedules its own revoke.
 */
export async function printUrlToDialog(
    url: string, contentType: string, fileName: string,
): Promise<DocumentPrintOutcome> {
    /* ⛔ A type the server WOULD serve but a frame cannot print. Open it rather
       than pretending — the browser's own viewer has a print button. */
    if (!framePrintable(contentType)) {
        window.open(url, '_blank', 'noopener');
        return {
            ok: true,
            via: 'tab',
            message: `${fileName} opened in a new tab — use the viewer's print button.`,
        };
    }

    const printed = await printInFrame(url);
    if (printed) {
        return {
            ok: true,
            via: 'dialog',
            message: `The print dialog is open for ${fileName}. Pick your printer there.`,
        };
    }

    /* ⚠️ THE FRAME REFUSED. ⛔ Do NOT revoke here — the tab about to open is
       reading this very URL, and `printInFrame` already schedules the revoke. */
    window.open(url, '_blank', 'noopener');
    return {
        ok: true,
        via: 'tab',
        message: `${fileName} opened in a new tab — this browser would not print it directly, `
               + 'so use the viewer\'s print button.',
    };
}
